import React, { useMemo, useState } from "react";
import { gql } from "@apollo/client";
import { useHrisRoleSidebarQuery } from "./HrisRoleDetailSidebar.generated";
import SidebarHeader, {
  SidebarContent,
  SidebarTitle,
} from "@src/features/sidebars/ui/SidebarHeader";
import InfoTable, { InfoTableItem } from "@src/lib/ui/InfoTable";
import SidebarLink from "@src/features/sidebars/ui/SidebarLink";
import ClickableLinkText from "@src/lib/ui/ClickableLinkText";
import CommaSeparatedLinks from "@src/features/sidebars/ui/CommaSeparatedLinks";
import { format, parseISO } from "date-fns";
import Text from "@src/lib/ui/Text";
import Link from "next/link";
import { cn } from "@src/lib/utils/cn";
import { DetailSidebarType } from "@src/features/sidebars/detailSidebar";
import PaginatedTable from "@src/lib/ui/PaginatedTable";
import {
  HrisEmployeeFragment,
  useHrisEmployeesLazyQuery,
  useHrisEmployeesQuery,
} from "./HrisEmployeeListContainer.generated";
import { HrisEmployeesInput } from "@src/types.generated";
import {
  QueryTabs,
  QueryTabRenderFns,
  QueryTabCopy,
  QueryTabCounts,
} from "@src/lib/ui/QueryTabs";
import { useToast } from "@src/lib/hooks/useToast";
import useActionPermissions from "@src/features/app/hooks/useActionPermissions";
import LinkHrisTeamDialog from "@src/features/settings/dialogs/LinkHrisTeamDialog";
import { DeleteHrisRoleDialog } from "./DeleteHrisRoleDialog";
import { Button } from "@src/lib/ui/Button";
import { useDeleteHrisTeamsMutation } from "./HrisRoleListContainer.generated";
import { GraphqlOperations } from "@src/types.generated";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@src/lib/ui/DropdownMenu";
import DotsIcon from "@src/lib/icons/18px/dots";
import LinkIcon from "@src/lib/icons/18px/link";
import TrashOutlineIcon from "@src/lib/icons/18px/trash-outline";
import IdBadge2OutlineIcon from "@src/lib/icons/18px/id-badge-2-outline";
import UserOutlineIcon from "@src/lib/icons/18px/user-outline";

type Props = {
  rowId: number;
  showHeader?: boolean;
};

const HrisRoleDetailSidebar: React.FC<Props> = ({
  rowId,
  showHeader = true,
}) => {
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [deleteRoleOpen, setDeleteRoleOpen] = useState(false);
  const { addToast } = useToast();
  const { userActionPermissions } = useActionPermissions();
  const [deleteHrisTeams] = useDeleteHrisTeamsMutation({
    refetchQueries: [GraphqlOperations.Query.HrisTeams],
    onCompleted: () => {
      addToast({ message: "HRIS roles deleted successfully" });
    },
  });

  const { data } = useHrisRoleSidebarQuery({
    variables: {
      id: rowId,
    },
    skip: !rowId,
  });

  const inputFilters: HrisEmployeesInput = useMemo(() => {
    return {
      filters: {
        roleIds: { value: [rowId] },
        employmentStatus: { value: "ACTIVE" },
      },
    };
  }, [rowId]);

  // Fetch total count for the tab badge
  const { data: employeesCountData } = useHrisEmployeesQuery({
    variables: {
      input: inputFilters,
      pagination: { limit: 0 }, // Only need the count, not the data
    },
    skip: !rowId,
  });

  const columns = [
    "name",
    "hrisId",
    "employmentStatus",
    "opusUser",
    "location",
    "role",
    "syncedAt",
  ] as const;
  type Column = (typeof columns)[number];

  const tabCounts: QueryTabCounts<"details" | "employees"> = useMemo(() => {
    return {
      employees: employeesCountData?.HrisEmployees?.totalCount ?? undefined,
    };
  }, [employeesCountData]);

  const role = data?.HrisTeam;
  if (!role) return null;

  const tabKeys = ["details", "employees"] as const;
  type TabKey = (typeof tabKeys)[number];

  const tabLabels: QueryTabCopy<TabKey> = {
    details: "Details",
    employees: "Active HRIS employees",
  };

  const tabQueryTabRenderFns: QueryTabRenderFns<TabKey> = {
    details: () => <InfoTable items={items} />,
    employees: () => (
      <PaginatedTable<
        HrisEmployeeFragment,
        Column,
        typeof useHrisEmployeesQuery,
        typeof useHrisEmployeesLazyQuery,
        Column
      >
        defaultSort={{ column: "syncedAt", descending: true }}
        queryHook={useHrisEmployeesQuery}
        lazyQueryHook={useHrisEmployeesLazyQuery}
        getItems={(x) => x?.HrisEmployees?.objects ?? []}
        getTotalItemCount={(x) => x?.HrisEmployees?.totalCount ?? 0}
        itemId={(x) => x.id}
        input={inputFilters}
        sidebarConfig={{
          tableId: "hris-role-employees",
          sidebarType: DetailSidebarType.HrisEmployee,
          enabled: true,
        }}
        disableRowClickOnColumns={new Set(["location", "role"])}
        allColumns={[...columns]}
        columnSelection={{
          enabled: true,
          alwaysVisibleColumns: ["name"],
          defaultSelectedColumns: columns.filter(
            (col) => col !== "employmentStatus",
          ),
        }}
        columnTitles={{
          name: "Name",
          hrisId: "HRIS ID",
          employmentStatus: "HRIS Status",
          opusUser: "Opus user",
          location: "HRIS Location",
          role: "HRIS Role",
          syncedAt: "Synced from HRIS",
        }}
        renderFns={{
          name: (x) => (
            <div className="flex flex-1 items-center gap-2">
              <UserOutlineIcon className="text-gray-7" />
              <Text
                variant="compact-title"
                ellipsis
                ellipsisParentFlexDirection={"row"}
              >
                {`${x.firstName} ${x.lastName}`}
              </Text>
            </div>
          ),
          location: (x) => (
            <CommaSeparatedLinks
              items={x.locationMemberships.map((memb) => ({
                id: memb.location.id,
                name: memb.location.name,
              }))}
              sidebarType={DetailSidebarType.HrisLocation}
              tableId="hris-locations"
            />
          ),
          role: (x) => (
            <CommaSeparatedLinks
              items={x.teamMemberships.map((memb) => ({
                id: memb.team.id,
                name: memb.team.name,
              }))}
              sidebarType={DetailSidebarType.HrisRole}
              tableId="hris-roles"
            />
          ),
          employmentStatus: (x) => {
            const status = x.employmentStatus || "--";
            const isActive = status === "ACTIVE";
            return (
              <Text
                variant="compact-description"
                className={isActive ? "" : "text-gray-7"}
              >
                {status}
              </Text>
            );
          },
          opusUser: (x) =>
            x.user?.name ? (
              <Link
                href={{
                  pathname: "/users/[id]",
                  query: {
                    id: x.user.id.toString(),
                  },
                }}
                className={cn("flex items-center no-underline text-[unset]")}
                target="_blank"
                rel="noopener noreferrer"
              >
                <ClickableLinkText>{x.user.name}</ClickableLinkText>
              </Link>
            ) : (
              <Text variant="compact-description" className="text-gray-7">
                Not linked
              </Text>
            ),
          syncedAt: (x) => format(parseISO(x.createdAt), "M/d/yyyy"),
          hrisId: (x) => x.remoteHrisId,
        }}
      />
    ),
  };

  const items: InfoTableItem[] = [
    {
      label: "Opus role",
      value: role.group?.name ? (
        <Link
          href={{
            pathname: "/roles/[id]",
            query: {
              id: role.group.id.toString(),
            },
          }}
          className={cn("flex items-center no-underline text-[unset]")}
          target="_blank"
          rel="noopener noreferrer"
        >
          <ClickableLinkText>{role.group.name}</ClickableLinkText>
        </Link>
      ) : (
        <Text variant="compact-description" className="text-gray-7">
          Not linked
        </Text>
      ),
    },
    {
      label: "Link method",
      value: role.group ? (
        role.manuallyLinked ? (
          "Manually linked"
        ) : (
          "Auto-linked"
        )
      ) : (
        <Text variant="compact-description" className="text-gray-7">
          Not linked
        </Text>
      ),
    },
    {
      label: "Active HRIS employees",
      value: `${role.activeHrisEmployeeCount} ${
        role.activeHrisEmployeeCount === 1 ? "employee" : "employees"
      }`,
    },
    {
      label: "HRIS integration",
      value: role.hrisIntegration?.id ? (
        <SidebarLink
          id={role.hrisIntegration.id}
          sidebarType={DetailSidebarType.HrisIntegration}
          tableId="hris-integrations-table"
        >
          {role.hrisIntegration.id}
        </SidebarLink>
      ) : (
        "--"
      ),
    } as InfoTableItem,
    {
      label: "First synced at",
      value: format(parseISO(role.createdAt), "M/d/yyyy h:mm a"),
    },
  ];

  return (
    <div className="flex h-full flex-col">
      {showHeader && <SidebarHeader />}
      <SidebarTitle
        title={role.name}
        icon={<IdBadge2OutlineIcon className="size-[18px] text-gray-9" />}
        sidebarType={DetailSidebarType.HrisRole}
      />
      <div className="px-4">
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => {
              setLinkDialogOpen(true);
            }}
            disabled={!userActionPermissions?.changeOrgSettings}
          >
            <LinkIcon className="text-muted-foreground" />
            {role.group ? "Edit opus role link" : "Link to opus role"}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon">
                <DotsIcon />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" side="bottom">
              <DropdownMenuItem
                className="cursor-pointer"
                disabled={!userActionPermissions?.changeOrgSettings}
                onClick={() => {
                  setDeleteRoleOpen(true);
                }}
              >
                <TrashOutlineIcon className="text-gray-10" />
                <Text variant="compact-title">Delete</Text>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <SidebarContent>
        <div className="flex min-h-0 flex-1 flex-col">
          <QueryTabs<TabKey>
            tabs={[...tabKeys]}
            labels={tabLabels}
            render={tabQueryTabRenderFns}
            counts={tabCounts as QueryTabCounts<TabKey>}
            defaultTab="details"
            queryParamKey="hris-role-tab"
          />
        </div>
      </SidebarContent>
      {linkDialogOpen && role && (
        <LinkHrisTeamDialog
          open={linkDialogOpen}
          onOpenChange={setLinkDialogOpen}
          hrisTeam={role}
        />
      )}
      {deleteRoleOpen && role && (
        <DeleteHrisRoleDialog
          open={deleteRoleOpen}
          onOpenChange={setDeleteRoleOpen}
          roleName={role.name}
          onDelete={async () => {
            await deleteHrisTeams({
              variables: { ids: [role.id] },
            });
          }}
        />
      )}
    </div>
  );
};

// GraphQL query for the sidebar - fetches a single HRIS team by ID
gql`
  query HrisRoleSidebar($id: Int!) {
    HrisTeam(id: $id) {
      ...HrisTeam
      manuallyLinked
      activeHrisEmployeeCount
      hrisIntegration {
        id
        hrisProvider
      }
    }
  }
`;

export default HrisRoleDetailSidebar;
